/**
 * # dxfBridge.polylineChildRecords
 *
 * The two projectors that take the CHILD records the dxf-io grouping pass
 * folds onto a legacy heavy `POLYLINE` — its `VERTEX` run and its terminating
 * `SEQEND` — and turn them into the typed records the model keeps
 * (`PolylineVertexRecord`, `PolylineSeqendMetadata`).
 *
 * ## Why they live in their own leaf
 *
 * They were extracted from `dxfBridge.drawingToDxf.ts` when carrying the whole
 * SEQEND record (PR #785 @codex round 2, F1) pushed that file past the
 * CLAUDE.md 2000-line ceiling it was itself split out to respect. This module
 * is a LEAF: it imports the shared point converter from
 * `./dxfBridge.entityConversion` and the typed records from `./model`, and
 * nothing imports back into it, so no cycle. `dxfBridge.drawingToDxf.ts`
 * re-exports both names and `dxfBridge.ts` re-exports that in turn, so every
 * existing import path is unchanged.
 *
 * Both projectors share one rule: a child record's layer / linetype / colour
 * are NOT independently editable in AutoCAD (ezdxf
 * `Polyline.on_layer_change` rewrites every child), so the export re-derives
 * what the parent owns — and everything that belongs to the CHILD alone, and
 * that nothing else can reconstruct, rides these records. Dropping it is a
 * silent deletion of user content on the first open-and-save.
 *
 * Source of truth: Autodesk DXF reference (POLYLINE / VERTEX / SEQEND) +
 * ezdxf 1.4.4 `entities/polyline.py`. AutoCAD MCP is BANNED.
 */
import type { DxfSeqendEntity, DxfVertexEntity } from "@dxf-ai-editor/dxf-io";

import { toDrawingPoint3D } from "./dxfBridge.entityConversion";
import type { PolylineSeqendMetadata, PolylineVertexRecord } from "./model";

/**
 * Project ONE parsed `VERTEX` record onto its typed `PolylineVertexRecord`.
 *
 * Used by BOTH the POLYLINE branch (the folded `geometry.vertices` run) and
 * the orphan-VERTEX branch (a malformed file whose VERTEX has no preceding
 * POLYLINE), so the two can never disagree about what a vertex carries.
 *
 * ## Everything the record owns is copied, deliberately
 *
 * The AcDbEntity fields (layer / linetype / lineweight / colour) are NOT
 * copied: AutoCAD rewrites every child VERTEX when the owning POLYLINE's
 * value changes (ezdxf `Polyline.on_layer_change` / `on_linetype_change`),
 * so the export re-derives them from the parent — storing a second,
 * independently-mutable copy would invent state AutoCAD does not have.
 *
 * Everything else is per-vertex data no other field can reconstruct, and it
 * is copied verbatim: the geometry, the handle, and the four passthrough
 * channels (`xdata`, `extensionDictionaryHandle`, `extraGroups` — which is
 * where the entity parser parks the `102 {ACAD_REACTORS … 102 }` block and
 * any unmapped group — and a non-canonical `subclassMarkers` extension).
 * Those four used to survive as verbatim byte passthrough while POLYLINE /
 * VERTEX were `unsupported-entity`; dropping them once the family became
 * typed silently DELETED user records on the first open-and-save, which is
 * the data loss this projection exists to prevent. `dxfBridge.
 * entityConversion.ts` re-emits each of them on the export leg.
 *
 * Empty channels are left ABSENT (never `[]`) so a vertex that carried
 * nothing round-trips as the same minimal record it was imported as.
 */
export function toPolylineVertexRecord(
  vertex: DxfVertexEntity,
): PolylineVertexRecord {
  const vg = vertex.geometry;
  const record: PolylineVertexRecord = {
    handle: vertex.common.handle ?? "",
    location: toDrawingPoint3D(vg.location),
  };
  if (vg.startWidth !== undefined) record.startWidth = vg.startWidth;
  if (vg.endWidth !== undefined) record.endWidth = vg.endWidth;
  if (vg.bulge !== undefined) record.bulge = vg.bulge;
  if (vg.flags !== undefined) record.flags = vg.flags;
  if (vg.curveFitTangent !== undefined) {
    record.curveFitTangent = vg.curveFitTangent;
  }
  if (vg.faceVertexIndices) {
    record.faceVertexIndices = [...vg.faceVertexIndices];
  }
  if (vg.vertexId !== undefined) record.vertexId = vg.vertexId;
  if (vertex.subclassMarkers.length > 0) {
    record.subclassMarkers = [...vertex.subclassMarkers];
  }
  if (vertex.extraGroups.length > 0) {
    record.extraGroups = vertex.extraGroups.map((group) => ({
      code: group.code,
      value: group.value,
    }));
  }
  if (vertex.xdata && vertex.xdata.length > 0) {
    record.xdata = vertex.xdata.map((entry) => ({
      appName: entry.appName,
      groups: entry.groups.map((group) => ({
        code: group.code,
        value: group.value,
      })),
    }));
  }
  if (vertex.common.extensionDictionaryHandle !== undefined) {
    record.extensionDictionaryHandle = vertex.common.extensionDictionaryHandle;
  }
  return record;
}

/**
 * Project the parsed terminating `SEQEND` onto the typed record the model
 * keeps beside `PolylineEntity.seqendHandle` — everything the export leg does
 * NOT re-derive from the owning POLYLINE.
 *
 * The carried value is the REST of the record: the derived keys are removed by
 * DESTRUCTURING, and `PolylineSeqendMetadata` is `Omit<…>` over that same key
 * set (see its declaration in `entityPolylineTableOle.ts`), so the two cannot
 * drift and a channel added to `DxfSeqendEntity` / `DxfCommonEntityProps`
 * later rides along instead of being silently deleted on the next save. That
 * silent-drop shape is exactly what produced the per-VERTEX loss one level
 * down (PR #785 round 1, `24196d9`).
 *
 * Returns `undefined` when the SEQEND carried nothing but its (derived)
 * identity, so a terminator with no records of its own round-trips as the
 * same minimal `{seqendHandle}` it always was — no empty object in the
 * document, and no diff on a drawing that has none.
 */
export function toPolylineSeqendMetadata(
  seqend: DxfSeqendEntity,
): PolylineSeqendMetadata | undefined {
  const {
    kind: _kind,
    type: _type,
    geometry: _geometry,
    common,
    ...rest
  } = seqend;
  const {
    handle: _handle,
    ownerHandle: _ownerHandle,
    space: _space,
    layoutName: _layoutName,
    forceLayerEmit: _forceLayerEmit,
    ...commonRest
  } = common;
  const metadata = { ...rest, common: { ...commonRest } };
  return seqendCarriesNothing(metadata) ? undefined : metadata;
}

/**
 * The ONE `100` marker a canonical SEQEND carries
 * (`CANONICAL_SUBCLASS_MARKERS.SEQEND = ["AcDbEntity"]`, dxf-io `writer.ts`).
 * The writer re-emits it for every entity from the TYPE alone, so its presence
 * on a parsed record says nothing about what the terminator authored — exactly
 * like the group-70-derived family markers `mergeSubclassMarkers` drops for a
 * VERTEX.
 */
const CANONICAL_SEQEND_SUBCLASS_MARKER = "AcDbEntity";

/**
 * True when the parsed terminator carried nothing the export cannot
 * reconstruct — no common property of its own, and no channel holding
 * anything but the canonical subclass marker. Such a SEQEND round-trips as
 * the same minimal `{seqendHandle}` record it always was, so no metadata is
 * stored and a drawing full of ordinary polylines gains nothing.
 *
 * Walks the REST of the record rather than three known channel names, and
 * treats ANY non-array value as carried, so a channel added to
 * `DxfSeqendEntity` later keeps the record instead of being dropped as
 * "empty" — the same reason `PolylineSeqendMetadata` is an `Omit`.
 */
function seqendCarriesNothing(metadata: PolylineSeqendMetadata): boolean {
  if (Object.keys(metadata.common).length > 0) return false;
  for (const [key, value] of Object.entries(
    metadata as unknown as Record<string, unknown>,
  )) {
    if (key === "common" || value === undefined) continue;
    // A future non-array channel is data: keep the whole record.
    if (!Array.isArray(value)) return false;
    const meaningful =
      key === "subclassMarkers"
        ? value.filter((marker) => marker !== CANONICAL_SEQEND_SUBCLASS_MARKER)
        : value;
    if (meaningful.length > 0) return false;
  }
  return true;
}
